import { useQuery } from "@apollo/react-hooks";
import { useEffect, useMemo, useState } from "react";
import { useToasts } from "react-toast-notifications";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import {
  GET_CONSULTORY_SERVICES,
  GET_QUOTATIONS_BY_APPOINTMENT,
  PRODUCT_BY_SPECIALTIES_AND_MEDICAL,
} from "./queries";

const QUOTES_TAB_ID = 6;
const NEW_QUOTE_TAB_ID = 1;
const CURRENT_QUOTES_TAB_ID = 2;

const INITIAL_STATE = {
  dataQuotationsByAppointment: [],
  servicesSeleted: [],
  modalIsOpen: false,
  selectedQuotation: {},
};

const sameId = (a, b) => a != null && b != null && Number(a) === Number(b);

const getQuotationTotal = (quotationsDetails = []) =>
  quotationsDetails.reduce(
    (total, item) =>
      total +
      (Number(item?.consultoryDifferent) || 0) *
        (Number(item?.availableQuantity) || 0),
    0,
  );

export default function useActions({
  medicalStaffId,
  specialityId,
  branchOfficeId,
  quotations,
  handleParentChange,
  appointmentArsId,
  activeTab,
  dataAddTabServices,
  appointmentId,
  activeTabFather,
  isPageModalHistory,
}) {
  const [state, setState] = useState(INITIAL_STATE);
  const { addToast } = useToasts();
  const { params } = useParams();

  const isQuotesTab = activeTabFather === QUOTES_TAB_ID;
  const skipServiceOptions =
    !isQuotesTab || activeTab !== NEW_QUOTE_TAB_ID || isPageModalHistory;
  const quotationsDetails = quotations?.quotationsDetails;

  const {
    data: { consultoryServices } = {},
    loading: loadingConsultoryServices,
  } = useQuery(GET_CONSULTORY_SERVICES, {
    skip: skipServiceOptions || !medicalStaffId,
    fetchPolicy: "network-only",
    variables: {
      relations: [
        "product",
        "consultoryServicesDetail",
        "consultoryServicesDetail.ars",
      ],
      where: { medicalStaffId, branchOfficeId, enabled: true },
    },
  });

  const {
    data: { productBySpecialtiesAndMedical: specialityProducts } = {},
    loading: loadingSpecialityProducts,
  } = useQuery(PRODUCT_BY_SPECIALTIES_AND_MEDICAL, {
    skip: skipServiceOptions || !specialityId,
    fetchPolicy: "network-only",
    variables: { where: { isAppointment: false, specialityId } },
  });

  const {
    data: { getQuotationsByAppointment } = {},
    loading: loadinggetQuotationsByAppointment,
  } = useQuery(GET_QUOTATIONS_BY_APPOINTMENT, {
    skip: !isQuotesTab || activeTab !== CURRENT_QUOTES_TAB_ID,
    fetchPolicy: "network-only",
    variables: {
      relations: [
        "quotations",
        "quotations.quotationsDetails",
        "quotations.quotationsDetails.product",
      ],
      where: {
        medicalStaffId,
        branchOfficeId,
        specialityId,
        getNotIdAppointment: Number(params) || undefined,
      },
    },
  });

  // Servicios configurados por el medico ("Mis Servicios") que pertenecen a la
  // especialidad de la cita y aun no estan en la cotizacion. Los que no tienen
  // diferencia habilitada para la ARS de la cita se muestran deshabilitados.
  const serviceOptions = useMemo(() => {
    const specialityProductIds = new Set(
      (specialityProducts || []).map((product) => Number(product?.id)),
    );
    const quotedProductIds = new Set(
      (quotationsDetails || []).map((item) => Number(item?.product?.id)),
    );

    return (consultoryServices || [])
      .filter(({ product }) => {
        const productId = Number(product?.id);
        return (
          specialityProductIds.has(productId) &&
          !quotedProductIds.has(productId)
        );
      })
      .map(({ product, consultoryServicesDetail }) => {
        const detail = consultoryServicesDetail?.find((item) =>
          sameId(item?.ars?.id, appointmentArsId),
        );

        return {
          product,
          detail,
          hasCoverage: Boolean(detail?.enabled && detail?.consultoryDifferent),
        };
      })
      .sort((a, b) => a.product.name.localeCompare(b.product.name));
  }, [appointmentArsId, consultoryServices, quotationsDetails, specialityProducts]);

  const updateQuotationsDetails = (details) =>
    handleParentChange("tabQuotes", details, "quotations", "quotationsDetails");

  const handleAddService = (option) => {
    if (!option) return;

    if (!option.hasCoverage) {
      addToast(
        `El servicio "${option.product.name}", no tiene cobertura para la ARS seleccionada.`,
        { appearance: "error", autoDismiss: true },
      );
      return;
    }

    const { detail, product } = option;
    updateQuotationsDetails([
      ...(quotationsDetails ?? []),
      {
        product,
        consultoryFee: detail.consultoryFee,
        insuredCoverage: detail.insuredCoverage,
        consultoryDifferent: detail.consultoryDifferent,
        insuredContrated: detail.insuredContrated,
        insuredCopay: detail.insuredCopay,
        differencePlusQuantity: detail.consultoryDifferent,
        quantity: 1,
      },
    ]);
    addToast("Servicio agregado correctamente", {
      appearance: "success",
      autoDismiss: true,
    });
  };

  const handleRemoveService = (productId) =>
    updateQuotationsDetails(
      (quotationsDetails ?? []).filter(
        (item) => !sameId(item?.product?.id, productId),
      ),
    );

  const handleSelectedQuotation = (quotation) => {
    if (!sameId(quotation?.arsId, appointmentArsId)) {
      addToast("La ars del servicio seleccionado es diferente al de la cita", {
        appearance: "error",
        autoDismiss: true,
      });
      return;
    }

    setState((prev) => ({
      ...prev,
      modalIsOpen: true,
      selectedQuotation: quotation,
    }));
  };

  const handleCloseQuotation = () =>
    setState((prev) => ({ ...prev, modalIsOpen: false, servicesSeleted: [] }));

  const handleAddServices = (serviceSelected) => {
    if (
      dataAddTabServices?.some((service) =>
        sameId(service.services?.id, serviceSelected.product.id),
      )
    ) {
      addToast("Este servicio ya existe en el Tab de Servicios!!!", {
        appearance: "error",
        autoDismiss: true,
      });
      return;
    }

    setState((prev) => ({
      ...prev,
      servicesSeleted: [...prev.servicesSeleted, serviceSelected],
    }));
  };

  const handleAddServicesFromQuotation = () => {
    handleParentChange(
      "tabServices",
      [
        ...(dataAddTabServices ?? []),
        ...state.servicesSeleted.map((service) => ({
          quotationId: service?.quotationId,
          services: service.product,
          quantity: service.availableQuantity,
          insuredContrated: service?.insuredContrated,
          insuredCoverage: service?.insuredCoverage,
          insuredCopay: service?.insuredCopay,
          consultoryFee: service?.consultoryFee,
          consultoryDifferent: service?.consultoryDifferent,
          appointmentId,
        })),
      ],
      "appointmentHistoryServices",
    );

    addToast("Servicios agregados correctamente", {
      appearance: "success",
      autoDismiss: true,
    });
    handleCloseQuotation();
  };

  useEffect(() => {
    if (!getQuotationsByAppointment || loadinggetQuotationsByAppointment) return;

    const dataQuotationsByAppointment = getQuotationsByAppointment
      .map(({ quotations: quotation }) => {
        const details = (quotation.quotationsDetails || []).map((detail) => ({
          ...detail,
          exist:
            detail?.wasBilled ||
            dataAddTabServices?.some(
              (service) =>
                sameId(service?.quotationId, detail.quotationId) &&
                sameId(service?.services?.id, detail?.product?.id),
            ),
        }));

        return {
          ...quotation,
          quotationsDetails: details,
          total: getQuotationTotal(details),
        };
      })
      .filter((quotation) =>
        quotation.quotationsDetails.some((detail) => !detail.exist),
      )
      .sort((a, b) => b.id - a.id);

    setState((prev) => ({
      ...prev,
      dataQuotationsByAppointment,
      selectedQuotation:
        dataQuotationsByAppointment.find((item) =>
          sameId(item.id, prev.selectedQuotation.id),
        ) || {},
    }));
  }, [dataAddTabServices, getQuotationsByAppointment, loadinggetQuotationsByAppointment]);

  return [
    {
      state,
      serviceOptions,
      loadingServiceOptions:
        loadingConsultoryServices || loadingSpecialityProducts,
      loadinggetQuotationsByAppointment,
    },
    {
      handleAddService,
      handleRemoveService,
      handleSelectedQuotation,
      handleCloseQuotation,
      handleAddServices,
      handleAddServicesFromQuotation,
    },
  ];
}
